import _ from 'lodash';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  generateIncrementAmountLimitNotes,
  isFundingRequestComponentMaximumConstraintAmountApproved,
  isIncrementAmountApproved,
  isMinimumAmountApproved,
  MaximumAmountPerPeriodRow,
  MaximumFrequencyRow,
  VerificationFundingConstraintRow,
  VerificationFundingRequestComponentTable,
  VerificationReportFundingRequestComponentTypes,
} from './VerificationFundingRequestTableComponents';
import {
  isSanitizedJSONWithNumberValues,
  VerificationApproveTablesMode,
} from './verificationTablesUtils';

export const VerificationFundingRequestComponent: React.FC<{
  fundingRequestComponentType: VerificationReportFundingRequestComponentTypes;
  fundingRequestComponentMechanics: CreditAgreement.DomainEntity.DrawRequestProperties &
    CreditAgreement.DomainEntity.CollectionsRecycleProperties;
  fundingRequestComponentTotalAmounts: Util.SanitizedJSON;
  fundingRequestComponentMaximumConstraintAmount: Util.Maybe<number>;
  reportingDate: Util.FullDateStr;
  creditFacilityId: string;
  viewMode: VerificationApproveTablesMode;
}> = ({
  fundingRequestComponentType,
  fundingRequestComponentMechanics,
  fundingRequestComponentTotalAmounts,
  fundingRequestComponentMaximumConstraintAmount,
  reportingDate,
  creditFacilityId,
  viewMode,
}) => {
  const {
    minimumAmount,
    maximumFrequency,
    maximumAmountPeriod,
    maximumAmountPerPeriod,
    incrementAmount,
    isAllowed,
  } = fundingRequestComponentMechanics;

  if (!isSanitizedJSONWithNumberValues(fundingRequestComponentTotalAmounts)) {
    return null;
  }

  const fundingRequestComponentTypeString = fundingRequestComponentType.substring(
    0,
    fundingRequestComponentType.length - 'TotalAmount'.length,
  );

  return (
    <VerificationFundingRequestComponentTable
      viewMode={viewMode}
      fundingRequestComponentType={fundingRequestComponentTypeString}
      fundingRequestComponentTotalAmount={fundingRequestComponentTotalAmounts}
      fundingRequestComponentRows={
        <>
          {!_.isNil(fundingRequestComponentMaximumConstraintAmount) &&
            !_.isNil(fundingRequestComponentTotalAmounts.borrowerValue) && (
              <VerificationFundingConstraintRow
                constraintKey={`maximum ${fundingRequestComponentTypeString} amount`}
                requirement={commonUtils.numberToCurrencyWithSymbol({
                  number: fundingRequestComponentMaximumConstraintAmount,
                })}
                ifApproved={
                  isFundingRequestComponentMaximumConstraintAmountApproved({
                    fundingRequestComponentAmount:
                      fundingRequestComponentTotalAmounts.borrowerValue,
                    fundingRequestComponentMaximumConstraintAmount,
                  }).isApproved
                }
                isRoundingErrorAllowanceUsed={
                  isFundingRequestComponentMaximumConstraintAmountApproved({
                    fundingRequestComponentAmount:
                      fundingRequestComponentTotalAmounts.borrowerValue,
                    fundingRequestComponentMaximumConstraintAmount,
                  }).isRoundingErrorAllowanceUsed
                }
                limitNotes={{
                  limitValue: fundingRequestComponentTotalAmounts.borrowerValue,
                  limitValueType: 'currency',
                  limitType: 'max',
                }}
              />
            )}

          {!_.isNil(maximumAmountPerPeriod) &&
            !_.isNil(maximumAmountPeriod) &&
            !_.isNil(fundingRequestComponentTotalAmounts.borrowerValue) && (
              <MaximumAmountPerPeriodRow
                fundingRequestComponentTotalAmount={
                  fundingRequestComponentTotalAmounts.borrowerValue
                }
                fundingRequestComponentTypeKey={fundingRequestComponentType}
                maximumAmountPerPeriod={maximumAmountPerPeriod.value}
                maximumAmountPeriod={maximumAmountPeriod.value}
                reportingDate={reportingDate}
                creditFacilityId={creditFacilityId}
              />
            )}

          {!_.isNil(maximumFrequency) && (
            <MaximumFrequencyRow
              maximumFrequency={maximumFrequency}
              creditFacilityId={creditFacilityId}
              reportingDate={reportingDate}
              fundingRequestComponentTypeKey={fundingRequestComponentType}
            />
          )}

          {!_.isNil(incrementAmount) &&
            !_.isNil(fundingRequestComponentTotalAmounts.borrowerValue) && (
              <VerificationFundingConstraintRow
                constraintKey="incrementAmount"
                requirement={commonUtils.numberToCurrencyWithSymbol({
                  number: incrementAmount.value,
                })}
                ifApproved={isIncrementAmountApproved({
                  fundingRequestComponentAmount:
                    fundingRequestComponentTotalAmounts.borrowerValue,
                  incrementAmount: incrementAmount.value,
                })}
                limitNotes={{
                  limitValue: fundingRequestComponentTotalAmounts.borrowerValue,
                  limitValueType: 'currency',
                  limitType: 'min',
                }}
                limitNotesCustomString={generateIncrementAmountLimitNotes({
                  fundingRequestComponentAmount:
                    fundingRequestComponentTotalAmounts.borrowerValue,
                  fundingRequestComponentType,
                  incrementAmount: incrementAmount.value,
                })}
              />
            )}

          {!_.isNil(minimumAmount) &&
            !_.isNil(fundingRequestComponentTotalAmounts.borrowerValue) && (
              <VerificationFundingConstraintRow
                constraintKey="minimumAmount"
                requirement={commonUtils.numberToCurrencyWithSymbol({
                  number: minimumAmount.value,
                })}
                ifApproved={
                  isMinimumAmountApproved({
                    fundingRequestComponentAmount:
                      fundingRequestComponentTotalAmounts.borrowerValue,
                    minimumAmount: minimumAmount.value,
                  }).isApproved
                }
                isRoundingErrorAllowanceUsed={
                  isMinimumAmountApproved({
                    fundingRequestComponentAmount:
                      fundingRequestComponentTotalAmounts.borrowerValue,
                    minimumAmount: minimumAmount.value,
                  }).isRoundingErrorAllowanceUsed
                }
                limitNotes={{
                  limitValue: fundingRequestComponentTotalAmounts.borrowerValue,
                  limitValueType: 'currency',
                  limitType: 'min',
                }}
              />
            )}

          {!_.isNil(isAllowed) && (
            <VerificationFundingConstraintRow
              // eg. isCollectionsRecycleEnabled
              constraintKey={`Is ${fundingRequestComponentTypeString} Enabled`}
              ifApproved={!!isAllowed}
              requirement="Yes"
              current={isAllowed ? 'Yes' : 'No'}
              limitNotes={{
                limitValue: 0,
                limitValueType: 'number',
                limitType: 'min',
              }}
              limitNotesCustomString={`${commonUtils.toSentenceCase(
                fundingRequestComponentTypeString,
              )} is not enabled.`}
            />
          )}
        </>
      }
    />
  );
};
